// Platform-aware presentation helpers for the event-invite experience.
//
// IMPORTANT: Platform detection is used ONLY to tailor presentation (e.g. which
// CTA to surface). It must NEVER be used as an access-control mechanism — the
// website event preview remains fully usable on every platform.

export type Platform = 'ios' | 'android' | 'other';

/**
 * Android application package that claims the ridepulse.app event App Links.
 * Used only to build the Android CTA launcher intent — never for access control.
 */
export const ANDROID_PACKAGE = 'app.ridepulse.android';

/**
 * Detects the visitor's platform from a User-Agent string.
 *
 * Defaults to `navigator.userAgent` when no argument is supplied so it can be
 * used directly in components, while remaining pure/deterministic for tests.
 */
export function detectPlatform(
  userAgent: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''
): Platform {
  if (/android/i.test(userAgent)) return 'android';
  // iPadOS 13+ reports as Macintosh in the UA; the touch + Mac heuristic is the
  // standard way to keep iPad classified as iOS.
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
  if (/Macintosh/.test(userAgent) && typeof navigator !== 'undefined' && 'maxTouchPoints' in navigator && (navigator as any).maxTouchPoints > 0) {
    return 'ios';
  }
  return 'other';
}

/**
 * Builds the canonical HTTPS event URL for a given event, preserving any
 * invite `?code=` parameter.
 *
 * This is the SAME canonical URL contract used by the SPA route
 * (`/event/:eventId`) and the `og-image` edge function — there is no separate
 * Android URL scheme or Android-only event route. On Android, an installed
 * RidePulse app claims this HTTPS URL via App Links (`assetlinks.json`); when
 * the app is not installed the same URL keeps the event viewable on the website.
 */
export function buildCanonicalEventUrl(
  eventId: string,
  inviteCode: string | undefined,
  origin: string
): string {
  let url = `${origin}/event/${eventId}`;
  if (inviteCode) {
    url += `?code=${encodeURIComponent(inviteCode)}`;
  }
  return url;
}

/**
 * Builds a standards-compatible Android `intent://` launcher URL for the
 * Android-only "Open in RidePulse" CTA button.
 *
 * This is a CTA-launch mechanism ONLY — it is not a replacement URL format for
 * sharing, storage, analytics identity, routing, or iOS. The canonical shared
 * event URL remains normal HTTPS (`buildCanonicalEventUrl`).
 *
 * Format (Chrome intent: syntax):
 *   intent://{host}/{path}?{query}#Intent;scheme=https;package={pkg};S.browser_fallback_url={encoded};end
 *
 * - `scheme=https` + the host/path/query reconstruct the canonical HTTPS event
 *   URL as the intent data URI, so the installed app receives the exact same
 *   URL it would via an external App Link.
 * - `package` targets `app.ridepulse.android` so Chrome dispatches directly.
 * - `S.browser_fallback_url` is the URL-encoded canonical HTTPS event URL. If
 *   RidePulse is not installed, Chrome navigates to this URL, leaving the
 *   visitor on the functional website event page (with invite code preserved).
 */
/**
 * iOS custom-scheme deep link (`ridepulse://event/{id}`) with the invite code
 * encoded in the query — the canonical parser on iOS handles this route.
 */
export function buildIosEventUrl(eventId: string, inviteCode?: string): string {
  let url = `ridepulse://event/${eventId}`;
  if (inviteCode) {
    url += `?code=${encodeURIComponent(inviteCode)}`;
  }
  return url;
}

export function buildAndroidIntentUrl(
  eventId: string,
  inviteCode: string | undefined,
  origin: string
): string {
  const canonicalUrl = buildCanonicalEventUrl(eventId, inviteCode, origin);
  // Strip the scheme so we have just host + path + query.
  const hostAndPath = canonicalUrl.replace(/^https?:\/\//, '');
  const fallbackEncoded = encodeURIComponent(canonicalUrl);
  return (
    `intent://${hostAndPath}` +
    `#Intent;scheme=https;package=${ANDROID_PACKAGE}` +
    `;S.browser_fallback_url=${fallbackEncoded};end`
  );
}
